import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, ArrowCounterClockwise, ClipboardText, FileCsv, Plus, TreeStructure, WarningCircle } from '@phosphor-icons/react';
import {
  AnsichtUmschalter, BestaetigenDialog, FlipCard, ImmoButton, ListenSuche, ObjektFilter, SegmentedControl, Seitennavigation, ToastPill, useToastPill,
} from '../../../../components/immobilien/ui';
import { heuteIso } from '../../../../lib/immobilien/format';
import { mitViewTransition } from '../../../../lib/immobilien/viewTransition';
import { objektOptionen } from '../../../../lib/immobilien/objektOptionen';
import { seiteAus } from '../../../../lib/immobilien/seiten';
import {
  basisZeilen, baueDlZeilen, dlJeObjekt, dlKennzahlen, FLAG_FILTER, istFlagFilter, istStatusFilter, naechsteAblaeufe, passtFlag, passtStatus,
  STATUS_FILTER, zaehler, type FlagFilter, type StatusFilter,
} from '../../../../lib/dienstleister/liste';
import { useDienstleisterListe } from '../../../../lib/dienstleister/useDienstleisterListe';
import type { DlExportDaten } from '../../../../lib/dienstleister/export';
import type { VorschlagKandidat } from '../../../../lib/dienstleister/vorschlag';
import type { BestandDl } from '../../../../lib/dienstleister/neuerDienstleister';
import { NeuerDienstleisterFormular } from '../neu/NeuerDienstleisterFormular';
import { ImportPanel } from './ImportPanel';
import { ExportPanel } from './ExportPanel';
import { KategorienRegelnPanel } from './KategorienRegelnPanel';
import { DienstleisterWidgets } from './DienstleisterWidgets';
import { DienstleisterKachel } from './DienstleisterKachel';
import { DienstleisterTabelle } from './DienstleisterTabelle';

const PRO_SEITE = 20;
const KOPF_STIL: React.CSSProperties = { boxShadow: 'inset 0 0 0 1px var(--line)', fontWeight: 600 };
type ListenPanelArt = 'import' | 'export' | 'kategorien';

export default function DienstleisterListeSeite() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const heute = heuteIso();
  const { zustand, neuLaden } = useDienstleisterListe();
  const [suche, setSuche] = useState('');
  // Panel über der Liste (Handoff §7)
  // ?kategorien=1 (z. B. „Kategorien verwalten“ im Gewerke-Panel) öffnet das Panel direkt
  const [listenPanel, setListenPanel] = useState<ListenPanelArt | null>(() => (params.get('kategorien') === '1' ? 'kategorien' : null));
  useEffect(() => {
    if (params.get('kategorien') === '1') setParams(p => { const n = new URLSearchParams(p); n.delete('kategorien'); return n; }, { replace: true });
  }, [params, setParams]);
  const [formularGeaendert, setFormularGeaendert] = useState(false);
  const [verwerfen, setVerwerfen] = useState(false);
  const [toast, zeige] = useToastPill();
  const formularOffen = params.get('neu') === '1';
  const listeOben = useRef<HTMLDivElement>(null);

  const status: StatusFilter = istStatusFilter(params.get('f')) ? params.get('f') as StatusFilter : 'aktiv';
  const flag: FlagFilter = istFlagFilter(params.get('flag')) ? params.get('flag') as FlagFilter : 'alle';
  const objekt = params.get('obj') ?? 'alle';
  const ansicht = params.get('view') === 'liste' ? 'liste' : 'kacheln';

  const setzeParam = useCallback((schluessel: string, wert: string, standard: string) => {
    setParams(p => {
      const n = new URLSearchParams(p);
      if (wert === standard) n.delete(schluessel); else n.set(schluessel, wert);
      if (schluessel !== 'seite') n.delete('seite');
      return n;
    });
  }, [setParams]);
  const sucheAendern = (v: string) => {
    setSuche(v);
    if (params.get('seite')) setParams(p => { const n = new URLSearchParams(p); n.delete('seite'); return n; }, { replace: true });
  };

  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const zeilen = useMemo(() => (daten ? baueDlZeilen(daten, heute) : []), [daten, heute]);
  const k = useMemo(() => dlKennzahlen(zeilen), [zeilen]);
  const ablaeufe = useMemo(() => naechsteAblaeufe(zeilen), [zeilen]);
  const optionen = useMemo(() => (daten ? objektOptionen(daten.objekte, dlJeObjekt(zeilen)).filter(o => o.anzahl > 0) : []), [daten, zeilen]);
  const basis = useMemo(() => basisZeilen(zeilen, objekt, suche), [zeilen, objekt, suche]);
  const z = useMemo(() => zaehler(basis, status, flag), [basis, status, flag]);
  const sichtbar = useMemo(() => basis.filter(x => passtStatus(x, status) && passtFlag(x, flag)), [basis, status, flag]);

  const seiteRoh = Number(params.get('seite') ?? '1');
  const seite = useMemo(() => seiteAus(sichtbar, seiteRoh, PRO_SEITE), [sichtbar, seiteRoh]);
  const zuSeite = useCallback((n: number) => {
    setzeParam('seite', String(n), '1');
    listeOben.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [setzeParam]);

  const oeffne = useCallback((id: string) => {
    mitViewTransition(() => {
      navigate(`/admin/dienstleister/${encodeURIComponent(id)}`);
      window.scrollTo(0, 0);
    });
  }, [navigate]);

  // bis zur Drehkarte (E3) das bestehende Anlegen-Formular
  const exportDaten = useMemo<DlExportDaten | null>(() => (daten ? {
    roh: new Map(daten.dienstleister.map(d => [d.id, d as Record<string, unknown>])), dlObjekte: daten.dlObjekte, objekte: daten.objekte, nachweise: daten.nachweise,
  } : null), [daten]);
  const dlJeKategorie = useMemo(() => {
    const m = new Map<string, number>();
    for (const x of zeilen) for (const id of x.kategorieIds) m.set(id, (m.get(id) ?? 0) + 1);
    return m;
  }, [zeilen]);
  const kandidaten = useMemo<VorschlagKandidat[]>(() => {
    const vertretung = new Map((daten?.dienstleister ?? []).map(d => [d.id, d.vertretung === true]));
    return zeilen.map(x => ({ ...x, vertretung: vertretung.get(x.id) ?? false }));
  }, [zeilen, daten]);
  const zurListe = () => {
    setListenPanel(null);
    setSuche('');
    setParams(p => { const n = new URLSearchParams(p); ['f', 'flag', 'obj', 'seite'].forEach(x => n.delete(x)); return n; });
    listeOben.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Drehkarte „Neuer Dienstleister“ (Handoff §8); öffnen schließt die Panels über der Liste
  const schliesseFormular = useCallback(() => {
    setVerwerfen(false);
    setFormularGeaendert(false);
    setParams(p => { const n = new URLSearchParams(p); n.delete('neu'); return n; });
  }, [setParams]);
  const neuerDienstleister = () => {
    if (!formularOffen) {
      setListenPanel(null);
      setParams(p => { const n = new URLSearchParams(p); n.set('neu', '1'); return n; });
      return;
    }
    if (formularGeaendert) setVerwerfen(true); else schliesseFormular();
  };
  const panelUmschalten = (p: ListenPanelArt) => {
    if (formularOffen) { if (formularGeaendert) { setVerwerfen(true); return; } schliesseFormular(); }
    setListenPanel(akt => (akt === p ? null : p));
  };
  const gespeichert = useCallback((_id: string, meldung: string) => {
    setFormularGeaendert(false);
    setSuche('');
    setParams(p => { const n = new URLSearchParams(p); ['neu', 'f', 'flag', 'obj', 'seite'].forEach(x => n.delete(x)); return n; });
    neuLaden();
    zeige(meldung);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setParams, neuLaden, zeige]);
  const bestand = useMemo<BestandDl[]>(() => kandidaten.map(x => ({
    id: x.id, name: x.name, aktiv: x.aktiv, bevorzugt: x.bevorzugt, vertretung: x.vertretung ?? false, kategorieIds: x.kategorieIds,
  })), [kandidaten]);

  const datum = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
  const filterAktiv = !!suche || objekt !== 'alle' || status !== 'aktiv' || flag !== 'alle';

  return (
    <>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 14 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{datum}</span>
          <h1 className="m-0" style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05 }}>Dienstleister</h1>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>
            {daten ? `${k.aktiv} aktive Dienstleister · ${k.gewerke} Gewerke · ${k.offeneAuftraege} offene Aufträge` : 'Wird geladen …'}
          </span>
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton groesse={40} icon={FileCsv} onClick={() => panelUmschalten('import')} disabled={!daten} aria-expanded={listenPanel === 'import'} style={KOPF_STIL}>CSV-Import</ImmoButton>
          <ImmoButton groesse={40} icon={ClipboardText} onClick={() => panelUmschalten('export')} disabled={!daten} aria-expanded={listenPanel === 'export'} style={KOPF_STIL}>Export</ImmoButton>
          <ImmoButton groesse={40} icon={TreeStructure} onClick={() => panelUmschalten('kategorien')} disabled={!daten} aria-expanded={listenPanel === 'kategorien'} style={KOPF_STIL}>Kategorien &amp; Regeln</ImmoButton>
          <ImmoButton art={formularOffen ? 'fuellung' : 'akzent'} groesse={40} icon={Plus} iconGedreht={formularOffen} aria-expanded={formularOffen}
            onClick={neuerDienstleister} disabled={!daten} style={{ padding: '0 16px', ...(formularOffen ? { fontWeight: 800 } : {}) }}>
            {formularOffen ? 'Abbrechen' : 'Neuer Dienstleister'}
          </ImmoButton>
        </div>
      </header>

      {zustand.status === 'fehler' && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
          <WarningCircle weight="fill" size={28} color="var(--bad)" />
          <div className="flex flex-col flex-1" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Dienstleister konnten nicht geladen werden</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{zustand.meldung}</span>
          </div>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      )}

      {daten && listenPanel === 'import' && (
        <ImportPanel bestehende={daten.dienstleister} objekte={daten.objekte} kategorien={daten.kategorien} onSchliessen={() => setListenPanel(null)} onImportiert={neuLaden} onListe={zurListe} />
      )}
      {daten && exportDaten && listenPanel === 'export' && (
        <ExportPanel ansicht={sichtbar} alle={zeilen} daten={exportDaten} heute={heute} onSchliessen={() => setListenPanel(null)} />
      )}
      {daten && listenPanel === 'kategorien' && (
        <KategorienRegelnPanel kategorien={daten.kategorien} dlJeKategorie={dlJeKategorie} kandidaten={kandidaten}
          onSchliessen={() => setListenPanel(null)} onGeaendert={neuLaden} />
      )}

      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}

      {daten && (
        <FlipCard
          gedreht={formularOffen}
          vorne={(
            <DienstleisterWidgets k={k} ablaeufe={ablaeufe}
              onAktiv={() => setzeParam('f', 'aktiv', 'aktiv')} onOffen={() => setzeParam('f', 'rechnung', 'aktiv')} onDienstleister={oeffne} />
          )}
          hinten={formularOffen ? (
            <NeuerDienstleisterFormular kategorien={daten.kategorien} bestand={bestand} dlJeKategorie={dlJeKategorie} heute={heute}
              onGeaendert={setFormularGeaendert} onGespeichert={gespeichert} />
          ) : null}
        />
      )}
      {verwerfen && (
        <BestaetigenDialog titel="Änderungen verwerfen?" text="Die Eingaben für den neuen Dienstleister gehen verloren."
          bestaetigenLabel="Verwerfen" icon={ArrowCounterClockwise} onBestaetigen={schliesseFormular} onAbbrechen={() => setVerwerfen(false)} />
      )}

      <div ref={listeOben} className="flex flex-wrap items-center" style={{ gap: 10, scrollMarginTop: 16 }}>
        <ListenSuche wert={suche} onChange={sucheAendern} platzhalter="Name, Gewerk, Objekt, Ort …" ariaLabel="Dienstleister durchsuchen" />
        <SegmentedControl art="akzent" ariaLabel="Status" wert={status} onChange={f => setzeParam('f', f, 'aktiv')}
          segmente={STATUS_FILTER.map(f => ({ key: f.key, label: `${f.label} · ${z.status[f.key]}` }))} />
        <SegmentedControl art="akzent" ariaLabel="Merkmal" wert={flag} onChange={f => setzeParam('flag', f, 'alle')}
          segmente={FLAG_FILTER.map(f => ({ key: f.key, label: `${f.label} · ${z.flag[f.key]}` }))} />
        <ObjektFilter optionen={optionen} wert={objekt} onChange={id => setzeParam('obj', id, 'alle')} alleAnzahl={k.aktiv} />
        <AnsichtUmschalter wert={ansicht} onChange={a => setzeParam('view', a, 'kacheln')} />
      </div>

      {zustand.status === 'laedt' && (
        <section aria-busy="true" aria-label="Dienstleister werden geladen" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 230, borderRadius: 26, background: 'var(--fill2)' }} />)}
        </section>
      )}

      {daten && sichtbar.length === 0 && (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: 34, gap: 10, fontSize: 14, color: 'var(--ink3)' }}>
          {zeilen.length === 0 ? 'Noch keine Dienstleister angelegt' : 'Keine Dienstleister unter diesen Kriterien gefunden'}
          {filterAktiv && zeilen.length > 0 && (
            <ImmoButton onClick={() => { setSuche(''); setParams(p => { const n = new URLSearchParams(p); ['f', 'flag', 'obj', 'seite'].forEach(x => n.delete(x)); return n; }); }}>
              Filter zurücksetzen
            </ImmoButton>
          )}
        </div>
      )}

      {daten && sichtbar.length > 0 && ansicht === 'kacheln' && (
        <section aria-label="Dienstleister" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {seite.eintraege.map(x => <DienstleisterKachel key={x.id} z={x} onOeffnen={oeffne} />)}
        </section>
      )}
      {daten && sichtbar.length > 0 && ansicht === 'liste' && <DienstleisterTabelle zeilen={seite.eintraege} onOeffnen={oeffne} />}
      {daten && sichtbar.length > 0 && (
        <Seitennavigation seite={seite.seite} seiten={seite.seiten} von={seite.von} bis={seite.bis} gesamt={seite.gesamt} einheit="Dienstleister" onSeite={zuSeite} />
      )}
    </>
  );
}
